/**
 * TypeScript interfaces for Loyalty Sidebar Component
 * Following SOLID principles - Single Responsibility Principle
 * Each interface has a clear, single responsibility
 */

// Base types for loyalty system
export interface TierProgress {
  current: string;
  next: string | null;
  pointsRequired: number;
  pointsEarned: number;
  progressPercentage: number;
}

export interface LoyaltyStats {
  totalPoints: number;
  currentTier: string;
  nextTier: string | null;
  pointsToNextTier: number;
  progress: TierProgress;
}

export interface GettingStartedItem {
  id: string;
  title: string;
  completed: boolean;
  description?: string;
}

export interface LoyaltyData {
  stats: LoyaltyStats;
  gettingStarted: {
    title: string;
    subtitle: string;
    progress: number;
    items: GettingStartedItem[];
  };
}

// Service interfaces following Dependency Inversion Principle
export interface ILoyaltyDataProvider {
  getLoyaltyData(): Promise<LoyaltyData>;
}

export interface ITierCalculator {
  calculateProgress(points: number): TierProgress;
  getNextTierRequirements(currentTier: string): number;
  getTierName(points: number): string;
}

// Component prop interfaces
export interface LoyaltySidebarProps {
  className?: string;
  dataProvider?: ILoyaltyDataProvider;
  onExploreProgram?: () => void;
  onViewMore?: () => void;
  onCompleteTask?: (taskId: string) => void;
}

export interface LoyaltyHeaderProps {
  title: string;
  description: string;
  icon?: React.ReactNode;
}

export interface HeroCardProps {
  title: string;
  description: string;
  buttonText: string;
  onButtonClick: () => void;
  backgroundImage?: string;
}

export interface TierWidgetProps {
  tierData: {
    loyaltyTierId: number;
    loyaltyTierName: string;
    pointsEarned: number;
    pointsRequired: number;
    discountPercentage: number;
    hasReviewedOnGoogle: boolean;
    hasReviewedOnTrustPilot: boolean;
    loyaltyType: number;
  } | null;
  onViewMore?: () => void;
}

export interface GettingStartedProps {
  title: string;
  subtitle: string;
  progress: number;
  items: GettingStartedItem[];
}

// Enum for tier levels
export enum TierLevel {
  IRON = 'Iron',
  BRONZE = 'Bronze',
  SILVER = 'Silver',
  GOLD = 'Gold',
  PLATINUM = 'Platinum'
}

// Constants for tier thresholds
export const TIER_THRESHOLDS = {
  [TierLevel.IRON]: 0,
  [TierLevel.BRONZE]: 100,
  [TierLevel.SILVER]: 500,
  [TierLevel.GOLD]: 1500,
  [TierLevel.PLATINUM]: 5000
} as const;
